import { Route } from '@angular/router';

import { authGuard } from './guards/auth.guard';
import { campaignEditDirtyGuard } from './guards/campaign-edit-dirty.guard';
import { publicationEditDirtyGuard } from './guards/publication-edit-dirty.guard';

import { ComponentListComponent } from './components/pages/account/component-list/component-list.component';
import { LogoutComponent } from './components/pages/account/logout.component';
import { AccessRightByAssetListComponent } from './components/pages/assets/configuration/access-right-by-asset/access-right-by-asset-list.component';
import { CesCampaignDetailsComponent } from './components/pages/assets/configuration/campaigns/campaign-details.component';
import { CesCampaignsListComponent } from './components/pages/assets/configuration/campaigns/campaigns-list.component';
import { CampaignModelDetailComponent } from './components/pages/assets/configuration/campaigns/models/campaign-model-detail.component';
import { CampaignModelPlanningTabComponent } from './components/pages/assets/configuration/campaigns/models/campaign-model-planning-tab.component';
import { CampaignModelsListComponent } from './components/pages/assets/configuration/campaigns/models/campaign-models-list.component';
import { CesCampaignAuscultationComponent } from './components/pages/assets/configuration/campaigns/tabs/campaign-auscultation.component';
import { CesCampaignGeneralComponent } from './components/pages/assets/configuration/campaigns/tabs/campaign-general.component';
import { CesCampaignHistoryComponent } from './components/pages/assets/configuration/campaigns/tabs/campaign-history.component';
import { CesCampaignMeasuresComponent } from './components/pages/assets/configuration/campaigns/tabs/campaign-measures.component';
import { LocationListComponent } from './components/pages/assets/configuration/locations/locations-list.component';
import { AuscultationInfoDetailComponent } from './components/pages/assets/configuration/measures/auscultations/auscultation-info-detail.component';
import { AuscultationInfoPageComponent } from './components/pages/assets/configuration/measures/auscultations/auscultation-info-page.component';
import { AuscultationInformationsListComponent } from './components/pages/assets/configuration/measures/auscultations/auscultation-info-list.component';
import { ManualMeasureWizardComponent } from './components/pages/assets/configuration/measures/manual/new/manual-measure-wizard.component';
import { MeasureCheckerComponent } from './components/pages/assets/configuration/measures/measure-checker.component';
import { MeasureFormulaComponent } from './components/pages/assets/configuration/measures/measure-formula.component';
import { MeasureInfoComponent } from './components/pages/assets/configuration/measures/measure-info.component';
import { MeasuresComponent } from './components/pages/assets/configuration/measures/measures.component';
import { AutomaticMeasureWizardComponent } from './components/pages/assets/configuration/measures/automatic/new/automatic-measure-wizard.component';
import { ManualMeasureListComponent } from './components/pages/assets/configuration/measures/manual/manual-measure-list.component';
import { AutomaticMeasureListComponent } from './components/pages/assets/configuration/measures/automatic/automatic-measure-list.component';
import { MeasureDetailComponent } from './components/pages/assets/configuration/measures/shared/measure-detail.component';
import { DashboardComponent } from './components/pages/assets/monitoring/dashboard/dashboard.component';
import { MeasureAutomaticFollowUpListComponent } from './components/pages/assets/monitoring/automatic-measure/measure-automatic-follow-up-list.component';
import { MeasureAutomaticFollowUpDetailComponent } from './components/pages/assets/monitoring/automatic-measure/automatic-measure-detail/measure-automatic-follow-up-detail.component';
import { GraphViewComponent } from './components/pages/assets/monitoring/graphs/graph-view.component';
import { CesGraphsListComponent } from './components/pages/assets/monitoring/graphs/graphs-list.component';
import { CesTablesListComponent } from './components/pages/assets/monitoring/tables/tables-list.component';
import { TableViewComponent } from './components/pages/assets/monitoring/tables/table-view.component';
import { AssetGroupsListComponent } from './components/pages/configuration/asset-groups/asset-groups-list.component';
import { AssetTypesListComponent } from './components/pages/configuration/asset-types/asset-types-list.component';
import { ChoiceListsListComponent } from './components/pages/configuration/choice-lists/choice-lists-list.component';
import { FormulaModelsListComponent } from './components/pages/configuration/formula-models/formula-models-list.component';
import { MeasureTypesListComponent } from './components/pages/configuration/measure-types/measure-types-list.component';
import { RolesAndPermissionsComponent } from './components/pages/configuration/roles-and-permissions/roles-and-permissions.component';
import { UnitListComponent } from './components/pages/configuration/unit-list/unit-list.component';
import { UserDetailComponent } from './components/pages/configuration/users/user-detail/user-detail.component';
import { UsersComponent } from './components/pages/configuration/users/users.component';
import { Error404Component } from './components/pages/errors/error404/error404.component';
import { UnauthorizedComponent } from './components/pages/errors/unauthorized/unauthorized.component';
import { HomeComponent } from './components/pages/home/home.component';
import { UnderConstructionComponent } from './components/pages/under-construction/under-construction.component';
import { TestPermissionComponent } from './components/test-permission/test-permission.component';
import { AssetSidebarComponent } from './components/ui/sidebar/asset-sidebar/asset-sidebar.component';
import { ConfigurationSidebarComponent } from './components/ui/sidebar/configuration-sidebar/configuration-sidebar.component';

import { AdminGuard } from './guards/admin.guard';
import { PermissionGuard } from './guards/permission.guard';
import { AccessRightResolver } from './resolvers/access-right.resolver';
import { AssetGroupsResolver } from './resolvers/asset-groups.resolver';
import { AssetResolver } from './resolvers/asset.resolver';
import { ChoiceListsResolver } from './resolvers/choice-lists.resolver';
import { FormulaModelsResolver } from './resolvers/formula-models.resolver';
import { HomeResolver } from './resolvers/home.resolver';
import { LocationListsResolver } from './resolvers/location-lists.resolver';
import { MeasureTypesResolver } from './resolvers/measure-types.resolver';
import { PolynomialModelsResolver } from './resolvers/polynomial-models.resolver';
import { EventLogsListComponent } from './components/pages/event-logs/event-log-list.component';

export interface IconRoute extends Route {
  icon?: string;
  disabled?: boolean;
}

export const routes: Route[] = [
  {
    path: '',
    title: 'navigation.pages.home',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: HomeComponent,
        resolve: {
          asset: HomeResolver,
        },
      },
    ],
  },
  {
    path: 'test-permission',
    title: 'navigation.pages.testPermission',
    canActivate: [authGuard],
    component: TestPermissionComponent,
  },
  {
    path: '403',
    title: 'navigation.pages.forbiddenAccess',
    children: [
      {
        path: '',
        component: UnauthorizedComponent,
      },
    ],
  },
  {
    path: 'unauthorized',
    redirectTo: '403',
    pathMatch: 'full',
  },
  {
    path: 'forbidden',
    redirectTo: '403',
    pathMatch: 'full',
  },
  {
    path: '404',
    title: 'navigation.pages.notFound',
    children: [
      {
        path: '',
        component: Error404Component,
      },
    ],
  },
  {
    path: 'account',
    title: 'navigation.account.myAccount',
    children: [
      {
        path: '',
        title: 'navigation.account.myAccount',
        component: UnderConstructionComponent,
        icon: 'material-account_circle',
      },
      {
        path: 'component-list',
        title: 'navigation.account.componentList',
        icon: 'material-widgets',
        component: ComponentListComponent,
      },
      {
        path: 'settings',
        title: 'navigation.mySettings',
        icon: 'material-settings',
        component: UnderConstructionComponent,
      },
      {
        path: 'logout',
        title: 'navigation.logout',
        icon: 'material-logout',
        component: LogoutComponent,
      },
      { path: '**', redirectTo: '/404' },
    ] as IconRoute[],
  },
  {
    path: 'eventlogs',
    title: 'navigation.eventLogs',
    canActivate: [authGuard, AdminGuard],
    children: [
      {
        path: '',
        component: EventLogsListComponent,
      },
    ],
  },
  {
    path: 'configuration',
    title: 'navigation.config.settings',
    canActivate: [authGuard, AdminGuard],
    children: [
      {
        path: '',
        outlet: 'sidebar',
        component: ConfigurationSidebarComponent,
      },
      {
        path: 'definition',
        title: 'navigation.config.definitions',
        children: [
          {
            path: '',
            title: 'navigation.config.definitions',
            redirectTo: 'asset-groups',
            pathMatch: 'full',
          },
          {
            path: 'asset-groups',
            title: 'navigation.config.assetGroups',
            icon: 'material-source_environment',
            resolve: {
              asset: AssetGroupsResolver,
            },
            component: AssetGroupsListComponent,
          },
          {
            path: 'formula-models',
            title: 'navigation.config.formulaModels',
            icon: 'material-calculate',
            resolve: {
              resolvedData: FormulaModelsResolver,
            },
            component: FormulaModelsListComponent,
          },
          {
            path: 'polynomial-models',
            title: 'navigation.config.polynomialModels',
            icon: 'material-align_justify_space_between',
            resolve: {
              resolvedData: PolynomialModelsResolver,
            },
            component: FormulaModelsListComponent,
          },
          {
            path: 'choice-lists',
            title: 'navigation.config.choiceLists',
            icon: 'material-format_list_bulleted',
            resolve: {
              asset: ChoiceListsResolver,
            },
            component: ChoiceListsListComponent,
          },
          {
            path: 'asset-types',
            title: 'navigation.config.assetTypes',
            icon: 'material-water_damage',
            resolve: {
              asset: AssetGroupsResolver,
            },
            component: AssetTypesListComponent,
          },
          {
            path: 'measure-types',
            title: 'navigation.config.measureTypes',
            icon: 'material-instant_mix',
            resolve: {
              asset: MeasureTypesResolver,
            },
            component: MeasureTypesListComponent,
          },
          {
            path: 'measuring-devices',
            title: 'navigation.config.measuringDevices',
            icon: 'material-barcode_reader',
            component: UnderConstructionComponent,
            // Masqué tant que la fonctionnalité n'est pas livrée
            data: { neverDisplay: true },
          },
          {
            path: 'unit',
            title: 'navigation.config.units',
            icon: 'design_services',
            component: UnitListComponent,
          },
          { path: '**', redirectTo: '/404' },
        ] as IconRoute[],
      },
      {
        path: 'access-rights',
        title: 'navigation.config.access',
        children: [
          {
            path: 'users',
            title: 'navigation.config.users',
            icon: 'material-group',
            component: UsersComponent,
            children: [],
          },
          {
            path: 'users/:id',
            component: UserDetailComponent,
            data: {
              neverDisplay: true,
            },
          },
          {
            path: 'roles-and-permissions',
            title: 'navigation.config.rolesAndPermissions',
            icon: 'admin_panel_settings',
            component: RolesAndPermissionsComponent,
          },
          { path: '**', redirectTo: '/404' },
        ] as IconRoute[],
      },
      { path: '**', redirectTo: '/404' },
    ],
  },
  {
    path: 'assets',
    title: 'navigation.pages.assets',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: '/monitoring',
        pathMatch: 'full',
      },
      {
        path: ':id',
        resolve: {
          asset: AssetResolver,
        },
        children: [
          {
            path: '',
            redirectTo: 'monitoring/dashboard',
            pathMatch: 'full',
          },
          {
            path: '',
            outlet: 'sidebar',
            component: AssetSidebarComponent,
          },
          {
            path: 'monitoring',
            title: 'navigation.sections.monitoring',
            canActivateChild: [PermissionGuard],
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: 'dashboard',
                title: 'navigation.routes.dashboard',
                icon: 'material-dashboard',
                component: DashboardComponent,
              },
              {
                path: 'campaigns',
                title: 'navigation.campaigns',
                icon: 'material-date_range',
                children: [
                  {
                    path: '',
                    component: CesCampaignsListComponent,
                  },
                  {
                    path: 'models',
                    title: 'navigation.campaignRoutes.models',
                    children: [
                      {
                        path: '',
                        component: CampaignModelsListComponent,
                      },
                      {
                        path: ':modelId',
                        title: 'navigation.campaignRoutes.model',
                        component: CampaignModelDetailComponent,
                        children: [
                          {
                            path: '',
                            redirectTo: 'measures',
                            pathMatch: 'full',
                          },
                          {
                            path: 'measures',
                            title: 'navigation.campaignRoutes.tabs.measures',
                            component: CampaignModelPlanningTabComponent,
                            data: { mode: 'measures' },
                          },
                          {
                            path: 'auscultation',
                            title: 'navigation.campaignRoutes.tabs.auscultationInfo',
                            component: CampaignModelPlanningTabComponent,
                            data: { mode: 'auscultations' },
                          },
                          { path: '**', redirectTo: '/404' },
                        ],
                      },
                    ],
                  },
                  {
                    path: ':id',
                    title: 'navigation.campaignRoutes.campaign',
                    component: CesCampaignDetailsComponent,
                    canDeactivate: [campaignEditDirtyGuard],
                    children: [
                      {
                        path: '',
                        redirectTo: 'general',
                        pathMatch: 'full',
                      },
                      {
                        path: 'general',
                        title: 'navigation.campaignRoutes.tabs.general',
                        component: CesCampaignGeneralComponent,
                      },
                      {
                        path: 'measures',
                        title: 'navigation.campaignRoutes.tabs.measures',
                        component: CesCampaignMeasuresComponent,
                      },
                      {
                        path: 'auscultation',
                        title: 'navigation.campaignRoutes.tabs.auscultationInfo',
                        component: CesCampaignAuscultationComponent,
                      },
                      {
                        path: 'history',
                        title: 'navigation.campaignRoutes.tabs.history',
                        component: CesCampaignHistoryComponent,
                      },
                      { path: '**', redirectTo: '/404' },
                    ],
                  },
                ],
              },
              {
                path: 'automatic-measurements',
                title: 'navigation.routes.automaticMeasurements',
                icon: 'material-event_repeat',
                children: [
                  {
                    path: '',
                    component: MeasureAutomaticFollowUpListComponent,
                  },
                  {
                    path: ':id',
                    component: MeasureAutomaticFollowUpDetailComponent,
                  },
                ],
              },
              {
                path: 'analysis-posts',
                title: 'navigation.routes.analysisPosts',
                icon: 'material-event_repeat',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./components/pages/assets/monitoring/analysis-posts/analysis-post-follow-up-list.component').then(
                        m => m.AnalysisPostFollowUpListComponent,
                      ),
                  },
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./components/pages/assets/monitoring/analysis-posts/analysis-post-detail/analysis-post-detail.component').then(
                        m => m.AnalysisPostDetailComponent,
                      ),
                  },
                ],
              },
              { path: '**', redirectTo: '/404' },
            ],
          },
          {
            path: 'analysis',
            title: 'navigation.sections.analysis',
            canActivateChild: [PermissionGuard],
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: 'tables',
                title: 'navigation.routes.tables',
                icon: 'material-table',
                children: [
                  {
                    path: '',
                    component: CesTablesListComponent,
                  },
                  {
                    path: ':tableId/:tab',
                    component: TableViewComponent,
                  },
                  {
                    path: ':tableId',
                    redirectTo: ':tableId/data',
                    pathMatch: 'full',
                  },
                ],
              },
              {
                path: 'graphs',
                title: 'navigation.routes.graphs',
                icon: 'material-finance',
                children: [
                  {
                    path: '',
                    component: CesGraphsListComponent,
                  },
                  {
                    path: ':graphId',
                    component: GraphViewComponent,
                  },
                ],
              },
              {
                path: 'publications',
                title: 'navigation.routes.publications',
                icon: 'material-team_dashboard',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./components/pages/assets/analysis/publications/publications-list.component').then(
                        m => m.PublicationsListComponent,
                      ),
                  },
                  {
                    path: ':publicationId',
                    loadComponent: () =>
                      import('./components/pages/assets/analysis/publications/publication-detail.component').then(
                        m => m.PublicationDetailComponent,
                      ),
                    canDeactivate: [publicationEditDirtyGuard],
                  },
                ],
              },
              { path: '**', redirectTo: '/404' },
            ],
          },
          {
            path: 'configuration',
            title: 'navigation.sections.assetConfiguration',
            canActivateChild: [PermissionGuard],
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: 'locations',
                icon: 'material-distance',
                title: 'navigation.routes.locations',
                children: [
                  {
                    path: '',
                    component: LocationListComponent,
                    resolve: {
                      locations: LocationListsResolver,
                    },
                  },
                ],
              },
              {
                path: 'measures',
                title: 'navigation.measures',
                icon: 'material-format_list_bulleted',
                children: [
                  {
                    path: 'manual/new',
                    component: ManualMeasureWizardComponent,
                  },
                  {
                    path: 'manual/:id',
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    component: MeasureDetailComponent,
                    data: { kind: 'manual' },
                    children: [
                      { path: '', redirectTo: 'info', pathMatch: 'full' },
                      { path: 'info', component: MeasureInfoComponent },
                      { path: 'formula', component: MeasureFormulaComponent },
                      { path: 'verification', component: MeasureCheckerComponent },
                      { path: 'device', redirectTo: 'info', pathMatch: 'full' },
                      { path: '**', redirectTo: '/404' },
                    ],
                  },
                  {
                    path: 'automatic/new',
                    component: AutomaticMeasureWizardComponent,
                  },
                  {
                    path: 'automatic/:id',
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    component: MeasureDetailComponent,
                    data: { kind: 'automatic' },
                    children: [
                      { path: '', redirectTo: 'info', pathMatch: 'full' },
                      { path: 'info', component: MeasureInfoComponent },
                      { path: 'formula', component: MeasureFormulaComponent },
                      { path: 'verification', component: MeasureCheckerComponent },
                      { path: 'device', redirectTo: 'info', pathMatch: 'full' },
                      { path: '**', redirectTo: '/404' },
                    ],
                  },
                  {
                    path: 'analysis-post/new',
                    loadComponent: () =>
                      import('./components/pages/assets/configuration/measures/analysis-posts/new/analysis-post-wizard.component').then(
                        m => m.AnalysisPostWizardComponent,
                      ),
                  },
                  {
                    path: 'analysis-post/:id',
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                    component: MeasureDetailComponent,
                    data: { kind: 'analysis-post' },
                    children: [
                      { path: '', redirectTo: 'info', pathMatch: 'full' },
                      { path: 'info', component: MeasureInfoComponent },
                      { path: 'formula', component: MeasureFormulaComponent },
                      { path: 'verification', component: MeasureCheckerComponent },
                      { path: '**', redirectTo: '/404' },
                    ],
                  },
                  {
                    path: 'auscultation-information/new',
                    component: AuscultationInfoPageComponent,
                  },
                  {
                    path: 'auscultation-information/:auscId',
                    children: [
                      { path: '', redirectTo: 'info', pathMatch: 'full' },
                      { path: 'info', component: AuscultationInfoDetailComponent },
                      { path: '**', redirectTo: '/404' },
                    ],
                  },
                  {
                    path: 'order',
                    children: [
                      {
                        path: '',
                        loadComponent: () =>
                          import('./components/pages/assets/configuration/measures/order/measure-order.component').then(
                            m => m.MeasureOrderComponent,
                          ),
                        title: 'measureOrder.title',
                      },
                      {
                        path: ':measureTypeId',
                        loadComponent: () =>
                          import('./components/pages/assets/configuration/measures/order/measure-detail-order.component').then(
                            m => m.MeasureDetailOrderComponent,
                          ),
                        title: 'measureOrder.measureTitle',
                      },
                    ],
                  },
                  {
                    path: '',
                    component: MeasuresComponent,
                    children: [
                      { path: '', redirectTo: 'manual', pathMatch: 'full' },
                      { path: 'manual', component: ManualMeasureListComponent },
                      { path: 'automatic', component: AutomaticMeasureListComponent },
                      { path: 'auscultation-information', component: AuscultationInformationsListComponent },
                      {
                        path: 'analysis-post',
                        loadComponent: () =>
                          import('./components/pages/assets/configuration/measures/analysis-posts/new/analysis-post-list.component').then(
                            m => m.AnalysisPostListComponent,
                          ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'access-rights',
                icon: 'material-group',
                title: 'navigation.routes.accessRights',
                children: [
                  {
                    path: '',
                    component: AccessRightByAssetListComponent,
                    resolve: {
                      accessRights: AccessRightResolver,
                    },
                  },
                ],
              },
              {
                path: 'manual-imports',
                icon: 'material-upload_file',
                title: 'navigation.routes.manualImports',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./components/pages/assets/configuration/manual-imports/manual-imports-list.component').then(
                        m => m.ManualImportsListComponent,
                      ),
                  },
                  {
                    path: ':importId',
                    loadComponent: () =>
                      import('./components/pages/assets/configuration/manual-imports/manual-import-editor.component').then(
                        m => m.ManualImportEditorComponent,
                      ),
                  },
                ],
              },
              { path: '**', redirectTo: '/404' },
            ],
          },
          { path: '**', redirectTo: '/404' },
        ] as IconRoute[],
      },
    ],
  },
  { path: '**', redirectTo: '/404' },
];
